import { PageTitleRow } from "../../../components/layout/PageTitleRow";
import i18n from "../../../i18n";import { Trans } from "react-i18next";import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Plus, Pencil, Trash2, Lock } from "lucide-react";
import { AppLayout, PageBackLink } from "../../../components/layout";
import { Button } from "../../../components/ui/button";
import { ui } from "../../../components/ui";
import { LeaveActionToast } from "../../leave-management/components/LeaveActionToast";
import { TablePagination } from "../../leave-management/components/TablePagination";
import { fetchRoles, deleteRole } from "../../../services/rolesPermissionsService";
import { DeleteConfirmModal, DeleteBlockedModal } from "../components/Modals";

const TOAST_KEY = "roles_permissions_toast";
const PAGE_SIZE = 10;

function RolesMobileList({ roles, onEdit, onDelete }) {
  return (
    <ul className="divide-y divide-(--color-text-border)/35 md:hidden">
      {roles.map((role) =>
      <li key={role.id} className="px-4 py-3.5">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold text-primary truncate">{role.name}</p>
              <div className="mt-1.5 flex flex-wrap items-center gap-2">
                <span className="text-xs per-file-hint">
                  {role.usersAssigned} <Trans i18nKey="roles.user" />{role.usersAssigned !== 1 ? "s" : ""}
                </span>
              </div>
              {role.description ?
            <p className="mt-1.5 text-xs per-file-hint line-clamp-2">{role.description}</p> :
            null}
              <p className="mt-1 text-[11px] per-file-hint">{role.createdOn}</p>
            </div>
            <div className="flex shrink-0 flex-col gap-1.5">
              <button
              type="button"
              onClick={() => onEdit(role)}
              className="inline-flex items-center gap-1 rounded-lg border border-primary/20 bg-primary/10 px-2.5 py-1.5 text-xs font-medium text-primary hover:bg-primary/15 transition">
              
                <Pencil className="h-3.5 w-3.5" />
                <Trans i18nKey="common.edit" />
              </button>
              {role.type === "System" ?
            <button
              type="button"
              disabled
              className="inline-flex items-center gap-1 rounded-lg border border-(--color-text-border) bg-workspace/40 px-2.5 py-1.5 text-xs font-medium per-file-hint opacity-60 cursor-not-allowed">
              
                  <Lock className="h-3.5 w-3.5" />
                  <Trans i18nKey="common.delete" />
                </button> :

            <button
              type="button"
              onClick={() => onDelete(role)}
              className="inline-flex items-center gap-1 rounded-lg border border-error/25 bg-error/10 px-2.5 py-1.5 text-xs font-medium text-error hover:bg-error/15 transition">
              
                  <Trash2 className="h-3.5 w-3.5" />
                  <Trans i18nKey="common.delete" />
                </button>
            }
            </div>
          </div>
        </li>
      )}
    </ul>);

}

function RolesTable({ roles, rowOffset = 0, onEdit, onDelete }) {
  if (roles.length === 0) {
    return (
      <div className="px-6 py-12 text-center">
        <p className="text-sm per-file-hint"><Trans i18nKey="roles.noRolesMatchYourSearch" /></p>
      </div>);

  }

  return (
    <>
      <RolesMobileList roles={roles} onEdit={onEdit} onDelete={onDelete} />
      <div className="hidden md:block w-full overflow-x-auto">
        <table className="w-full text-sm min-w-[860px]">
          <thead>
            <tr className="text-left text-[10px] uppercase tracking-wider per-file-hint bg-workspace/30">
              <th className="px-4 py-3 font-semibold text-center w-12">#</th>
              <th className="px-4 py-3 font-semibold whitespace-nowrap"><Trans i18nKey="roles.roleName" /></th>
              <th className="px-4 py-3 font-semibold min-w-[200px]"><Trans i18nKey="common.description2" /></th>
              <th className="px-4 py-3 font-semibold text-center whitespace-nowrap"><Trans i18nKey="roles.usersAssigned" /></th>
              <th className="px-4 py-3 font-semibold whitespace-nowrap"><Trans i18nKey="dateTime.createdOn2" /></th>
              <th className="px-4 py-3 font-semibold text-center whitespace-nowrap"><Trans i18nKey="common.actions" /></th>
            </tr>
          </thead>
          <tbody>
            {roles.map((role, idx) =>
            <tr
              key={role.id}
              className="hover:bg-workspace/20 transition-colors border-b border-(--color-text-border)/35 last:border-b-0">
              
                <td className="px-4 py-3 text-center per-file-hint font-medium">{rowOffset + idx + 1}</td>
                <td className="px-4 py-3">
                  <span className="font-semibold text-primary">{role.name}</span>
                </td>
                <td className="px-4 py-3">
                  <span className="text-[13px] per-file-hint line-clamp-2 leading-relaxed">
                    {role.description}
                  </span>
                </td>
                <td className="px-4 py-3 text-center">
                  <span className="inline-flex items-center justify-center min-w-7 h-6 px-2 rounded-full bg-primary/10 text-primary text-[13px] font-semibold">
                    {role.usersAssigned}
                  </span>
                </td>
                <td className="px-4 py-3 text-xs per-file-hint whitespace-nowrap">{role.createdOn}</td>
                <td className="px-4 py-3">
                  <div className="flex items-center justify-center gap-1.5">
                    <button
                    type="button"
                    onClick={() => onEdit(role)}
                    title={i18n.t("formActions.editRole")}
                    className="inline-flex items-center gap-1 rounded-lg border border-primary/20 bg-primary/10 px-2.5 py-1.5 text-xs font-medium text-primary hover:bg-primary/15 transition">
                    
                      <Pencil className="h-3.5 w-3.5" />
                      <Trans i18nKey="common.edit" />
                    </button>
                    {role.type === "System" ?
                  <button
                    type="button"
                    disabled
                    title={i18n.t("validation.systemRolesCannotBeDeleted")}
                    className="inline-flex items-center gap-1 rounded-lg border border-(--color-text-border) bg-workspace/40 px-2.5 py-1.5 text-xs font-medium per-file-hint opacity-60 cursor-not-allowed">
                    
                        <Lock className="h-3.5 w-3.5" />
                        <Trans i18nKey="common.delete" />
                      </button> :

                  <button
                    type="button"
                    onClick={() => onDelete(role)}
                    title={role.usersAssigned > 0 ? `Remove this role from ${role.usersAssigned} employees first` : "Delete role"}
                    className="inline-flex items-center gap-1 rounded-lg border border-error/25 bg-error/10 px-2.5 py-1.5 text-xs font-medium text-error hover:bg-error/15 transition">
                    
                        <Trash2 className="h-3.5 w-3.5" />
                        <Trans i18nKey="common.delete" />
                      </button>
                  }
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </>);

}

export function RolesListPage() {
  const navigate = useNavigate();
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [toast, setToast] = useState(null);
  const [page, setPage] = useState(1);

  const loadRoles = () => {
    setLoading(true);
    fetchRoles().then(setRoles).catch(() => setError("Failed to load roles. Please try again.")).finally(() => setLoading(false));
  };

  useEffect(() => {loadRoles();}, []);
  useEffect(() => {
    const raw = sessionStorage.getItem(TOAST_KEY);
    if (!raw) return;
    try {
      setToast(JSON.parse(raw));
    } catch {
      setToast(null);
    }
    sessionStorage.removeItem(TOAST_KEY);
  }, []);

  useEffect(() => {
    if (!toast) return undefined;
    const timer = setTimeout(() => setToast(null), 4000);
    return () => clearTimeout(timer);
  }, [toast]);

  const filteredRoles = useMemo(() => {
    if (!searchQuery.trim()) return roles;
    const q = searchQuery.toLowerCase();
    return roles.filter((r) => r.name.toLowerCase().includes(q) || r.description.toLowerCase().includes(q));
  }, [roles, searchQuery]);

  useEffect(() => {
    setPage(1);
  }, [searchQuery]);

  const totalPages = Math.max(Math.ceil(filteredRoles.length / PAGE_SIZE), 1);
  const safePage = Math.min(page, totalPages);
  const rowOffset = (safePage - 1) * PAGE_SIZE;
  const visibleRoles = filteredRoles.slice(rowOffset, rowOffset + PAGE_SIZE);

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    setDeleteLoading(true);
    try {
      await deleteRole(deleteTarget.id);
      setToast({ message: `Role "${deleteTarget.name}" deleted.`, type: "success" });
      setDeleteTarget(null);
      loadRoles();
    } catch (err) {
      setToast({ message: err.message, type: "error" });
      setDeleteTarget(null);
    } finally {
      setDeleteLoading(false);
    }
  };

  const isBlocked = deleteTarget && deleteTarget.usersAssigned > 0;

  return (
    <AppLayout
      overlay={
      <>
          {deleteTarget && !isBlocked &&
        <DeleteConfirmModal
          role={deleteTarget}
          onConfirm={handleDeleteConfirm}
          onCancel={() => setDeleteTarget(null)}
          loading={deleteLoading} />

        }
          {deleteTarget && isBlocked &&
        <DeleteBlockedModal role={deleteTarget} onClose={() => setDeleteTarget(null)} />
        }
          <LeaveActionToast toast={toast} />
        </>
      }>
      
      <div className="mb-5">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <PageTitleRow back={<PageBackLink to="/" />}>
              <h1 className={ui.pageTitle}><Trans i18nKey="roles.rolesPermissions" /></h1>
            </PageTitleRow>
          </div>
          <Button
            onClick={() => navigate("/hrms-team/config/roles-services/create")}
            className="h-11 px-5 rounded-xl bg-primary text-white hover:bg-accent shadow-[0_8px_20px_-8px_rgba(30,51,131,0.5)] self-start sm:self-auto">
            
            <Plus className="h-4 w-4" />
            <Trans i18nKey="formActions.createRole" />
          </Button>
        </div>
      </div>

      <div className="bg-white rounded-2xl shadow-[0_10px_30px_-18px_rgba(30,51,131,0.35)] border border-white/60 overflow-hidden">
        <div className="px-4 sm:px-6 py-3 border-b border-(--color-text-border)/70 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <h2 className="text-lg font-medium text-foreground m-0"><Trans i18nKey="roles.manageRoles" /></h2>
          <div className="relative w-full sm:w-72 shrink-0">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 per-file-hint pointer-events-none" />
            <input data-page-search="true"
              className="w-full h-9 rounded-xl border border-(--color-text-border) pl-9 pr-3 text-sm text-foreground outline-hidden focus:border-accent focus:ring-2 focus:ring-accent/10 transition"
              type="text"
              placeholder={i18n.t("formActions.searchRolesByName")}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)} />
            
          </div>
        </div>
        {loading ?
        <div className="px-6 py-8">
            <div className="flex flex-col gap-3">
              {[1, 2, 3, 4].map((n) =>
            <div
              key={n}
              className="h-11 rounded-lg bg-workspace/50 animate-pulse" />

            )}
            </div>
          </div> :
        error ?
        <div className="px-6 py-8 text-sm text-error">{error}</div> :

        <>
          <RolesTable
            roles={visibleRoles}
            rowOffset={rowOffset}
            onEdit={(role) => navigate(`/hrms-team/config/roles-services/edit/${String(role.id)}`)}
            onDelete={setDeleteTarget} />

          {filteredRoles.length > 0 &&
          <TablePagination
            page={safePage}
            totalPages={totalPages}
            totalItems={filteredRoles.length}
            pageSize={PAGE_SIZE}
            onPageChange={setPage} />
          }
        </>

        }
      </div>
    </AppLayout>);

}

export { TOAST_KEY };
